/**
 * @fileoverview Live Stadium Stage and HUD Component for Tug Of War / Estira y Afloja (/screen).
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (60 FPS, 0% CPU in idle),
 * physically calculated rope displacement, tension micro-wobble, 3-tier character fatigue evolution,
 * sudden death K.O. physics, cinematic mud splash fall, victory fanfare and ephemeral confetti.
 * 
 * @module components/Screen/Sections/TugOfWar/TugOfWarGame
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, GetTheme, CalculateRopeDisplacement, DeterminePhaseTier } from '../../../../utilities/BL/TugOfWar';
import {
    resolveAssetUrl,
    preloadImageUrls,
    TugOfWarAudioSynth,
    DEFAULT_TUG_BG_SVG,
    DEFAULT_ROPE_SVG,
    DEFAULT_ROPE_FLAG_SVG,
    DEFAULT_MUD_PUDDLE_SVG,
    DEFAULT_PLAYER1_PHASE1_SVG,
    DEFAULT_PLAYER1_PHASE2_SVG,
    DEFAULT_PLAYER1_PHASE3_SVG,
    DEFAULT_PLAYER1_LOSER_SVG,
    DEFAULT_PLAYER1_WINNER_SVG,
    DEFAULT_PLAYER2_PHASE1_SVG,
    DEFAULT_PLAYER2_PHASE2_SVG,
    DEFAULT_PLAYER2_PHASE3_SVG,
    DEFAULT_PLAYER2_LOSER_SVG,
    DEFAULT_PLAYER2_WINNER_SVG
} from './TugOfWarAssets';

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_GAME_CONFIGURATION = {
    gameBackgroundType: 'color',
    gameBackgroundColor: '#0f172a',
    gameBackgroundUrl: '',
    gameTextColor: '#ffffff',
    fontFamily: 'Montserrat',
    player1Name: 'EQUIPO AZUL',
    player2Name: 'EQUIPO ROJO',
    player1Color: '#00f0ff',
    player2Color: '#ff0055',
    roundDuration: 15,
    displacementStyle: 'DynamicBias' // 'DynamicBias' | 'VisualSuspense'
};

/**
 * Confetti colors palette for victory explosion.
 * @constant {Array<string>}
 */
const CONFETTI_COLOR_PALETTE = ['#00f0ff', '#ff0055', '#fde047', '#38bdf8', '#fb7185', '#ffffff', '#4ade80', '#c084fc'];

/**
 * TugOfWarGame Component (/screen - Componente 47.1)
 * 
 * @function TugOfWarGame
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - Incoming WebSocket packet or snapshot from parent Screen router.
 * @param {Object} [props.currentMatchState] - Match state passed directly by Screen.js.
 * @param {Object} [props.theme] - Theme object directly passed from Screen.js.
 * @param {Object} [props.config] - Config object directly passed from Screen.js.
 * @param {string} [props.roomId] - Room GUID.
 * @returns {React.ReactElement} The rendered stadium game stage and HUD.
 */
export const TugOfWarGame = ({
    payload,
    currentMatchState,
    theme: propTheme,
    config: propConfig,
    roomId
}) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA VISUAL Y CONFIGURACIÓN
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);

    // =========================================================================
    // 2. ESTADO DEL MOTOR DE JUEGO (TELEMETRÍA EN TIEMPO REAL)
    // =========================================================================
    const [gameState, setGameState] = useState(() => {
        const sourceData = currentMatchState || payload?.payload || payload?.data || payload;
        const initialPhase = (
            sourceData?.phase ||
            sourceData?.status ||
            sourceData?.state ||
            'idle'
        ).toLowerCase();

        const incomingCountdown = sourceData?.countdownNumber ??
            sourceData?.countdownSeconds ??
            sourceData?.count ??
            sourceData?.countdown ??
            3;

        const roundDuration = sourceData?.roundDuration || sourceData?.durationSeconds || 15;
        const incomingRemaining = sourceData?.remainingSeconds ??
            sourceData?.secondsRemaining ??
            sourceData?.durationSeconds ??
            roundDuration;

        const score1 = sourceData?.score1 ?? sourceData?.player1Score ?? sourceData?.scoreJ1 ?? 0;
        const score2 = sourceData?.score2 ?? sourceData?.player2Score ?? sourceData?.scoreJ2 ?? 0;

        const totalTaps1 = sourceData?.totalTaps1 ?? sourceData?.player1TotalTaps ?? 0;
        const totalTaps2 = sourceData?.totalTaps2 ?? sourceData?.player2TotalTaps ?? 0;

        const activeFans1 = sourceData?.activeFans1 ?? sourceData?.player1ActiveFans ?? 0;
        const activeFans2 = sourceData?.activeFans2 ?? sourceData?.player2ActiveFans ?? 0;

        const averagePull1 = sourceData?.averagePull1 ?? sourceData?.player1AveragePull ?? 0;
        const averagePull2 = sourceData?.averagePull2 ?? sourceData?.player2AveragePull ?? 0;

        const winner = sourceData?.winner ?? sourceData?.winnerSide ?? null;
        const isSuddenDeath = Boolean(sourceData?.isSuddenDeath ?? sourceData?.suddenDeath ?? false);
        const displacementStyle = sourceData?.displacementStyle || 'DynamicBias';

        return {
            phase: initialPhase,
            countdownNumber: incomingCountdown,
            roundDuration,
            remainingSeconds: incomingRemaining,
            score1,
            score2,
            totalTaps1,
            totalTaps2,
            activeFans1,
            activeFans2,
            averagePull1,
            averagePull2,
            winner,
            isSuddenDeath,
            displacementStyle
        };
    });

    // =========================================================================
    // 3. ESTADOS VISUALES Y CINEMÁTICAS (ZERO-CANVAS GPU)
    // =========================================================================
    const [isAudioMuted, setIsAudioMuted] = useState(TugOfWarAudioSynth.isMuted());
    const [showVictoryBanner, setShowVictoryBanner] = useState(false);
    const [ephemeralConfetti, setEphemeralConfetti] = useState([]);
    const [ropeDisplacementVw, setRopeDisplacementVw] = useState(0);
    const [microWobbleVw, setMicroWobbleVw] = useState(0);

    // Referencias para timers y control de transiciones de sonido
    const previousPhaseRef = useRef('');
    const previousCountdownRef = useRef(null);
    const victoryTimerRef = useRef(null);
    const confettiTimerRef = useRef(null);
    const wobbleIntervalRef = useRef(null);

    // =========================================================================
    // 4. SINCRONIZACIÓN DE TEMA VISUAL Y CONFIGURACIÓN
    // =========================================================================
    const fetchActiveTheme = useCallback(async () => {
        try {
            const configRes = await GetConfig(roomId);
            if (configRes && configRes.success && configRes.data) {
                const roomCfg = configRes.data;
                if (roomCfg.activeTheme) {
                    setActiveThemeData(roomCfg.activeTheme);
                } else if (roomCfg.activeThemeId) {
                    const themeRes = await GetTheme(roomCfg.activeThemeId, roomId);
                    if (themeRes && themeRes.success && themeRes.data) {
                        setActiveThemeData(themeRes.data);
                    }
                }
            }
        } catch (e) {
            // Non-blocking
        }
    }, [roomId]);

    useEffect(() => {
        if (propTheme) {
            const directTheme = propTheme.theme && typeof propTheme.theme === 'object' ? propTheme.theme : propTheme;
            setActiveThemeData(directTheme);
            return;
        }

        if (propConfig) {
            const confTheme = propConfig.theme || propConfig.activeTheme;
            if (confTheme) {
                setActiveThemeData(confTheme);
                return;
            }
        }

        if (currentMatchState?.theme) {
            setActiveThemeData(currentMatchState.theme);
            return;
        }

        if (payload?.theme || payload?.data?.theme || payload?.payload?.theme) {
            setActiveThemeData(payload.theme || payload.data?.theme || payload.payload?.theme);
            return;
        }

        if (!activeThemeData) {
            fetchActiveTheme();
        }
    }, [payload, propTheme, propConfig, currentMatchState, activeThemeData, fetchActiveTheme]);

    // =========================================================================
    // 5. PRECARGA DE ASSETS EN MEMORIA RAM
    // =========================================================================
    useEffect(() => {
        if (!activeThemeData) return;

        const assetUrlsToPreload = [
            activeThemeData.gameBackgroundUrl,
            activeThemeData.ropeTextureUrl,
            activeThemeData.ropeFlagUrl,
            activeThemeData.mudPuddleUrl,
            activeThemeData.player1Phase1Avatar,
            activeThemeData.player1Phase2Avatar,
            activeThemeData.player1Phase3Avatar,
            activeThemeData.player1LoserAvatar,
            activeThemeData.player1WinnerAvatar,
            activeThemeData.player2Phase1Avatar,
            activeThemeData.player2Phase2Avatar,
            activeThemeData.player2Phase3Avatar,
            activeThemeData.player2LoserAvatar,
            activeThemeData.player2WinnerAvatar
        ];

        preloadImageUrls(assetUrlsToPreload);
    }, [activeThemeData]);

    // =========================================================================
    // 6. SINCRONIZACIÓN DE TELEMETRÍA Y ESTADO DE PARTIDA
    // =========================================================================
    useEffect(() => {
        const sourceData = currentMatchState || payload?.payload || payload?.data || payload;
        if (!sourceData) return;

        setGameState((prev) => {
            const incomingPhase = (
                sourceData.phase ||
                sourceData.status ||
                sourceData.state ||
                prev.phase
            ).toLowerCase();

            const incomingCountdown = sourceData.countdownNumber ??
                sourceData.countdownSeconds ??
                sourceData.count ??
                sourceData.countdown ??
                prev.countdownNumber;

            const incomingRemaining = sourceData.remainingSeconds ??
                sourceData.secondsRemaining ??
                sourceData.durationSeconds ??
                prev.remainingSeconds;

            const score1 = sourceData.score1 ?? sourceData.player1Score ?? sourceData.scoreJ1 ?? prev.score1;
            const score2 = sourceData.score2 ?? sourceData.player2Score ?? sourceData.scoreJ2 ?? prev.score2;

            const totalTaps1 = sourceData.totalTaps1 ?? sourceData.player1TotalTaps ?? prev.totalTaps1;
            const totalTaps2 = sourceData.totalTaps2 ?? sourceData.player2TotalTaps ?? prev.totalTaps2;

            const activeFans1 = sourceData.activeFans1 ?? sourceData.player1ActiveFans ?? prev.activeFans1;
            const activeFans2 = sourceData.activeFans2 ?? sourceData.player2ActiveFans ?? prev.activeFans2;

            const averagePull1 = sourceData.averagePull1 ?? sourceData.player1AveragePull ?? prev.averagePull1;
            const averagePull2 = sourceData.averagePull2 ?? sourceData.player2AveragePull ?? prev.averagePull2;

            const winner = sourceData.winner ?? sourceData.winnerSide ?? prev.winner;
            const isSuddenDeath = Boolean(sourceData.isSuddenDeath ?? sourceData.suddenDeath ?? prev.isSuddenDeath);
            const displacementStyle = sourceData.displacementStyle || prev.displacementStyle;
            const roundDuration = sourceData.roundDuration || sourceData.durationSeconds || prev.roundDuration;

            return {
                ...prev,
                phase: incomingPhase,
                countdownNumber: incomingCountdown,
                remainingSeconds: incomingRemaining,
                roundDuration,
                score1,
                score2,
                totalTaps1,
                totalTaps2,
                activeFans1,
                activeFans2,
                averagePull1,
                averagePull2,
                winner,
                isSuddenDeath,
                displacementStyle
            };
        });
    }, [currentMatchState, payload]);

    // =========================================================================
    // 7. MOTOR DE CÁLCULO DE DESPLAZAMIENTO FÍSICO Y MICRO-VAIVÉN
    // =========================================================================
    useEffect(() => {
        if (gameState.phase === 'finished') {
            // Cinemática final fija de desplazamiento extremo al lodo (31vw)
            const winnerSide = String(gameState.winner || '').toLowerCase();
            if (winnerSide === 'player1' || winnerSide === 'j1' || winnerSide === 'equipo1' || winnerSide === 'team1') {
                setRopeDisplacementVw(-31); // Equipo 2 cae al centro (sobre el lodo)
            } else if (winnerSide === 'player2' || winnerSide === 'j2' || winnerSide === 'equipo2' || winnerSide === 'team2') {
                setRopeDisplacementVw(31);  // Equipo 1 cae al centro (sobre el lodo)
            } else {
                setRopeDisplacementVw(0);   // Empate
            }
            return;
        }

        if (gameState.phase === 'playing' || gameState.phase === 'paused') {
            const maxVw = gameState.isSuddenDeath ? 30 : 10;
            const calculatedDisplacement = CalculateRopeDisplacement(
                gameState.score1 || gameState.totalTaps1,
                gameState.score2 || gameState.totalTaps2,
                gameState.displacementStyle,
                maxVw
            );
            setRopeDisplacementVw(calculatedDisplacement);
        } else {
            setRopeDisplacementVw(0);
        }
    }, [
        gameState.phase,
        gameState.score1,
        gameState.score2,
        gameState.totalTaps1,
        gameState.totalTaps2,
        gameState.winner,
        gameState.isSuddenDeath,
        gameState.displacementStyle
    ]);

    // Micro-vaivén de tensión física a 60 FPS durante 'playing' (±1.5vw)
    useEffect(() => {
        if (gameState.phase === 'playing') {
            wobbleIntervalRef.current = setInterval(() => {
                const randomOffset = (Math.random() * 3 - 1.5); // de -1.5vw a +1.5vw
                setMicroWobbleVw(Math.round(randomOffset * 100) / 100);
            }, 120);
        } else {
            setMicroWobbleVw(0);
            if (wobbleIntervalRef.current) {
                clearInterval(wobbleIntervalRef.current);
                wobbleIntervalRef.current = null;
            }
        }

        return () => {
            if (wobbleIntervalRef.current) {
                clearInterval(wobbleIntervalRef.current);
            }
        };
    }, [gameState.phase]);

    // =========================================================================
    // 8. CONTROL DE AUDIO FX Y CINEMÁTICA FINAL
    // =========================================================================
    // 8.1 Tick de cuenta regresiva
    useEffect(() => {
        if (gameState.phase === 'countdown') {
            if (previousCountdownRef.current !== gameState.countdownNumber && gameState.countdownNumber > 0) {
                TugOfWarAudioSynth.playTick(gameState.countdownNumber === 1 ? 1200 : 880);
            }
        }
        previousCountdownRef.current = gameState.countdownNumber;
    }, [gameState.phase, gameState.countdownNumber]);

    // 8.2 Crujido de tensión periódica durante playing
    useEffect(() => {
        if (gameState.phase === 'playing') {
            const tensionIntensity = Math.min(1.0, Math.abs(ropeDisplacementVw) / 10 + 0.3);
            TugOfWarAudioSynth.playRopeTension(tensionIntensity);
        }
    }, [gameState.phase, ropeDisplacementVw]);

    // 8.3 Transición de fases y cinemática de victoria / caída al lodo
    useEffect(() => {
        const currentPhase = gameState.phase;

        if (previousPhaseRef.current !== currentPhase) {
            if (currentPhase === 'playing') {
                TugOfWarAudioSynth.playGoHorn();
                setShowVictoryBanner(false);
                setEphemeralConfetti([]);
            } else if (currentPhase === 'finished') {
                // Sonido inmediato de chapoteo en el lodo (splat)
                TugOfWarAudioSynth.playMudSplash();

                // A los 1.5s exactos (al completar el glide de 1.5s cubic-bezier):
                if (victoryTimerRef.current) clearTimeout(victoryTimerRef.current);
                victoryTimerRef.current = setTimeout(() => {
                    setShowVictoryBanner(true);
                    TugOfWarAudioSynth.playVictoryCheer();

                    // Disparar lluvia de 40 partículas de confeti efímero
                    const confettiParticles = Array.from({ length: 40 }, (_, idx) => ({
                        id: `confetti_${Date.now()}_${idx}`,
                        left: `${Math.random() * 96 + 2}%`,
                        color: CONFETTI_COLOR_PALETTE[idx % CONFETTI_COLOR_PALETTE.length],
                        size: `${Math.random() * 14 + 10}px`,
                        delay: `${Math.random() * 0.8}s`,
                        duration: `${Math.random() * 1.5 + 1.8}s`,
                        rotation: `${Math.random() * 360}deg`
                    }));

                    setEphemeralConfetti(confettiParticles);

                    // Auto-destrucción total del confeti a los 3000ms
                    if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
                    confettiTimerRef.current = setTimeout(() => {
                        setEphemeralConfetti([]);
                    }, 3000);
                }, 1500);
            }
            previousPhaseRef.current = currentPhase;
        }

        return () => {
            if (victoryTimerRef.current) clearTimeout(victoryTimerRef.current);
            if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
        };
    }, [gameState.phase]);

    // =========================================================================
    // 9. EXTRACCIÓN DE ATRIBUTOS Y TEMATIZACIÓN
    // =========================================================================
    const fontFamily = activeThemeData?.fontFamily || DEFAULT_GAME_CONFIGURATION.fontFamily;
    const gameBackgroundType = activeThemeData?.gameBackgroundType || DEFAULT_GAME_CONFIGURATION.gameBackgroundType;
    const gameBackgroundColor = activeThemeData?.gameBackgroundColor || DEFAULT_GAME_CONFIGURATION.gameBackgroundColor;
    const gameBackgroundUrl = resolveAssetUrl(
        activeThemeData?.gameBackgroundUrl,
        gameBackgroundType === 'image' ? DEFAULT_TUG_BG_SVG : ''
    );

    const player1Name = activeThemeData?.player1Name || DEFAULT_GAME_CONFIGURATION.player1Name;
    const player2Name = activeThemeData?.player2Name || DEFAULT_GAME_CONFIGURATION.player2Name;
    const player1Color = activeThemeData?.player1Color || DEFAULT_GAME_CONFIGURATION.player1Color;
    const player2Color = activeThemeData?.player2Color || DEFAULT_GAME_CONFIGURATION.player2Color;

    const ropeTextureUrl = resolveAssetUrl(activeThemeData?.ropeTextureUrl, DEFAULT_ROPE_SVG);
    const ropeFlagUrl = resolveAssetUrl(activeThemeData?.ropeFlagUrl, DEFAULT_ROPE_FLAG_SVG);
    const mudPuddleUrl = resolveAssetUrl(activeThemeData?.mudPuddleUrl, DEFAULT_MUD_PUDDLE_SVG);

    // =========================================================================
    // 10. DETERMINACIÓN DE SPRITES SEGÚN TERCIOS DE TIEMPO / FASES
    // =========================================================================
    const { p1CurrentSprite, p2CurrentSprite } = useMemo(() => {
        const isFinished = gameState.phase === 'finished';
        const winnerSide = String(gameState.winner || '').toLowerCase();

        // 1. Estado Finalizado (Winner / Loser Sprites)
        if (isFinished) {
            if (winnerSide === 'player1' || winnerSide === 'j1' || winnerSide === 'equipo1' || winnerSide === 'team1') {
                return {
                    p1CurrentSprite: resolveAssetUrl(activeThemeData?.player1WinnerAvatar, DEFAULT_PLAYER1_WINNER_SVG),
                    p2CurrentSprite: resolveAssetUrl(activeThemeData?.player2LoserAvatar, DEFAULT_PLAYER2_LOSER_SVG)
                };
            }
            if (winnerSide === 'player2' || winnerSide === 'j2' || winnerSide === 'equipo2' || winnerSide === 'team2') {
                return {
                    p1CurrentSprite: resolveAssetUrl(activeThemeData?.player1LoserAvatar, DEFAULT_PLAYER1_LOSER_SVG),
                    p2CurrentSprite: resolveAssetUrl(activeThemeData?.player2WinnerAvatar, DEFAULT_PLAYER2_WINNER_SVG)
                };
            }
            // Empate
            return {
                p1CurrentSprite: resolveAssetUrl(activeThemeData?.player1Phase1Avatar, DEFAULT_PLAYER1_PHASE1_SVG),
                p2CurrentSprite: resolveAssetUrl(activeThemeData?.player2Phase1Avatar, DEFAULT_PLAYER2_PHASE1_SVG)
            };
        }

        // 2. Evolución en 3 Tercios de Tiempo Durante la Partida
        const duration = Math.max(1, gameState.roundDuration || 15);
        const remaining = Math.max(0, gameState.remainingSeconds ?? duration);
        const timeRatio = remaining / duration; // 1.0 -> 0.0

        let tier = 1;
        if (timeRatio >= 0.67) {
            tier = 1; // Primer tercio: Fase 1 (Energía normal)
        } else if (timeRatio >= 0.34) {
            tier = 2; // Segundo tercio: Fase 2 (Esfuerzo y sudor)
        } else {
            tier = 3; // Tercer tercio: Fase 3 (Tensión máxima)
        }

        const p1Sprite = tier === 1
            ? resolveAssetUrl(activeThemeData?.player1Phase1Avatar, DEFAULT_PLAYER1_PHASE1_SVG)
            : tier === 2
                ? resolveAssetUrl(activeThemeData?.player1Phase2Avatar, DEFAULT_PLAYER1_PHASE2_SVG)
                : resolveAssetUrl(activeThemeData?.player1Phase3Avatar, DEFAULT_PLAYER1_PHASE3_SVG);

        const p2Sprite = tier === 1
            ? resolveAssetUrl(activeThemeData?.player2Phase1Avatar, DEFAULT_PLAYER2_PHASE1_SVG)
            : tier === 2
                ? resolveAssetUrl(activeThemeData?.player2Phase2Avatar, DEFAULT_PLAYER2_PHASE2_SVG)
                : resolveAssetUrl(activeThemeData?.player2Phase3Avatar, DEFAULT_PLAYER2_PHASE3_SVG);

        return {
            p1CurrentSprite: p1Sprite,
            p2CurrentSprite: p2Sprite
        };
    }, [
        gameState.phase,
        gameState.winner,
        gameState.roundDuration,
        gameState.remainingSeconds,
        activeThemeData
    ]);

    // =========================================================================
    // 11. MANEJADOR DEL TOGGLE MUTE DE AUDIO
    // =========================================================================
    const handleAudioToggle = () => {
        const nextMuteState = !isAudioMuted;
        TugOfWarAudioSynth.setMuted(nextMuteState);
        setIsAudioMuted(nextMuteState);
        if (!nextMuteState) {
            TugOfWarAudioSynth.unlock();
        }
    };

    // Desplazamiento total aplicado al Stage Rig (Cuerda + Jugadores)
    const totalDisplacementVw = gameState.phase === 'finished'
        ? ropeDisplacementVw
        : ropeDisplacementVw + microWobbleVw;

    return (
        <div
            className="tugofwar-screen tugofwar-game"
            style={{ fontFamily: `${fontFamily}, sans-serif` }}
        >
            {/* Capa 1: Fondo Dinámico GPU Composited */}
            {gameBackgroundType === 'image' && gameBackgroundUrl ? (
                <div
                    className="tugofwar-game__background-image"
                    style={{ backgroundImage: `url(${gameBackgroundUrl})` }}
                />
            ) : (
                <div
                    className="tugofwar-game__background-color"
                    style={{
                        backgroundColor: gameBackgroundColor,
                        backgroundImage: `radial-gradient(circle at 50% 30%, rgba(30, 27, 75, 0.9) 0%, rgba(2, 6, 23, 0.98) 100%)`
                    }}
                />
            )}

            {/* Capa 2: Efectos de Iluminación y Haces de Luz */}
            <div className="tugofwar-game__stadium-lighting">
                <div className="tugofwar-game__light-beam tugofwar-game__light-beam--left" />
                <div className="tugofwar-game__light-beam tugofwar-game__light-beam--right" />
            </div>

            {/* Capa 3: HUD Superior a 60 FPS */}
            <header className="tugofwar-hud">
                {/* Equipo 1 HUD */}
                <div className="tugofwar-hud__team tugofwar-hud__team--p1">
                    <div
                        className="tugofwar-hud__team-name"
                        style={{ color: player1Color, textShadow: `0 0 16px ${player1Color}88` }}
                    >
                        {player1Name}
                    </div>
                    {/* <div className="tugofwar-hud__stats">
                        <span className="tugofwar-hud__fans-badge">
                            👥 {gameState.activeFans1} Fans
                        </span>
                        <span className="tugofwar-hud__score-badge" style={{ borderColor: player1Color }}>
                            ⚡ {Math.round(gameState.score1 || gameState.totalTaps1)}
                        </span>
                    </div> */}
                </div>

                {/* Temporizador Central y Estado */}
                <div className="tugofwar-hud__center">
                    <div className={`tugofwar-hud__timer-box ${gameState.remainingSeconds <= 5 && gameState.phase === 'playing' ? 'tugofwar-hud__timer-box--danger' : ''}`}>
                        <span className="tugofwar-hud__timer-value">
                            {gameState.phase === 'countdown' ? (
                                `¡${gameState.countdownNumber}!`
                            ) : (
                                `${Math.max(0, Math.ceil(gameState.remainingSeconds))}s`
                            )}
                        </span>
                    </div>
                    {gameState.isSuddenDeath && (
                        <div className="tugofwar-hud__sudden-death-badge">
                            ⚡ ¡MUERTE SÚBITA! ⚡
                        </div>
                    )}
                </div>

                {/* Equipo 2 HUD */}
                <div className="tugofwar-hud__team tugofwar-hud__team--p2">
                    <div
                        className="tugofwar-hud__team-name"
                        style={{ color: player2Color, textShadow: `0 0 16px ${player2Color}88` }}
                    >
                        {player2Name}
                    </div>
                    {/* <div className="tugofwar-hud__stats">
                        <span className="tugofwar-hud__score-badge" style={{ borderColor: player2Color }}>
                            ⚡ {Math.round(gameState.score2 || gameState.totalTaps2)}
                        </span>
                        <span className="tugofwar-hud__fans-badge">
                            👥 {gameState.activeFans2} Fans
                        </span>
                    </div> */}
                </div>

                {/* Botón Maestro Toggle de Audio */}
                <button
                    type="button"
                    className={`tugofwar-audio-btn ${isAudioMuted ? 'tugofwar-audio-btn--muted' : 'tugofwar-audio-btn--unmuted'}`}
                    onClick={handleAudioToggle}
                    title={isAudioMuted ? 'Activar Sonido FX' : 'Silenciar Sonido'}
                    aria-label="Toggle Sonido"
                >
                    {isAudioMuted ? '🔇' : '🔊'}
                </button>
            </header>

            {/* Capa 4: Escenario de Batalla Zero-Canvas (Charco de Lodo Fijo + Rig Móvil) */}
            <main className="tugofwar-game__arena">
                {/* 4.1 Charco de Lodo Central Fijo (Posicionado exactamente a left: 50%, transform: translateX(-50%)) */}
                <div className="tugofwar-mud">
                    <img
                        src={mudPuddleUrl}
                        alt="Charco de Lodo"
                        className="tugofwar-mud__image"
                    />
                    <div className="tugofwar-mud__center-line" />
                </div>

                {/* 4.2 Rig Cinemático Desplazable con GPU Layer (translate3d a 60 FPS) */}
                <div
                    className={`tugofwar-game__stage-rig ${gameState.phase === 'finished' ? 'tugofwar-game__stage-rig--finished' : ''}`}
                    style={{
                        transform: `translate3d(${totalDisplacementVw}vw, 0, 0)`,
                        willChange: 'transform'
                    }}
                >
                    {/* Personaje Equipo 1 (Izquierda) */}
                    <div className="tugofwar-player tugofwar-player--p1">
                        <img
                            src={p1CurrentSprite}
                            alt={player1Name}
                            className="tugofwar-player__avatar"
                        />
                    </div>

                    {/* Cuerda Física Horizontal que cruza el Escenario */}
                    <div
                        className="tugofwar-rope"
                        style={{ backgroundImage: `url(${ropeTextureUrl})` }}
                    >
                        {/* Pañuelo / Listón Marcador Central (#flag) */}
                        <div className="tugofwar-rope__flag-anchor" id="flag">
                            <img
                                src={ropeFlagUrl}
                                alt="Marcador Pañuelo"
                                className="tugofwar-rope__flag-img"
                            />
                        </div>
                    </div>

                    {/* Personaje Equipo 2 (Derecha) */}
                    <div className="tugofwar-player tugofwar-player--p2">
                        <img
                            src={p2CurrentSprite}
                            alt={player2Name}
                            className="tugofwar-player__avatar"
                        />
                    </div>
                </div>
            </main>

            {/* Capa 5: Overlay de Cuenta Regresiva (3, 2, 1) */}
            {gameState.phase === 'countdown' && (
                <div className="tugofwar-countdown-overlay">
                    <div className="tugofwar-countdown-overlay__number">
                        {gameState.countdownNumber > 0 ? gameState.countdownNumber : '¡YA!'}
                    </div>
                </div>
            )}

            {/* Capa 6: Overlay de Pausa */}
            {gameState.phase === 'paused' && (
                <div className="tugofwar-paused-overlay">
                    <div className="tugofwar-paused-overlay__card">
                        <h2>⏸️ PARTIDA EN PAUSA</h2>
                        <p>ESPERANDO REANUDACIÓN POR EL ADMINISTRADOR...</p>
                    </div>
                </div>
            )}

            {/* Capa 7: Banner de Victoria Final Proyectado a los 1.5s */}
            {gameState.phase === 'finished' && showVictoryBanner && (
                <div className="tugofwar-banner">
                    <div className="tugofwar-banner__card">
                        <img src='/img/page/xolos-five.png' style={{ width: '450px' }} />
                        {gameState.winner === 'player1' ? (
                            <>
                                <div className="tugofwar-banner__header mt-3">EL GANADOR ES...</div>
                                <h1
                                    className="tugofwar-banner__winner-name"
                                    style={{ color: player1Color, textShadow: `0 0 30px ${player1Color}` }}
                                >
                                    🏆 {player1Name} 🏆
                                </h1>
                                {/* <div className="tugofwar-banner__avatar-wrap">
                                    <img
                                        src={resolveAssetUrl(activeThemeData?.player1WinnerAvatar, DEFAULT_PLAYER1_WINNER_SVG)}
                                        alt={player1Name}
                                        className="tugofwar-banner__avatar"
                                    />
                                </div> */}
                            </>
                        ) : gameState.winner === 'player2' ? (
                            <>
                                <div className="tugofwar-banner__header mt-3">EL GANADOR ES...</div>
                                <h1
                                    className="tugofwar-banner__winner-name"
                                    style={{ color: player2Color, textShadow: `0 0 30px ${player2Color}` }}
                                >
                                    🏆 {player2Name} 🏆
                                </h1>
                                {/* <div className="tugofwar-banner__avatar-wrap">
                                    <img
                                        src={resolveAssetUrl(activeThemeData?.player2WinnerAvatar, DEFAULT_PLAYER2_WINNER_SVG)}
                                        alt={player2Name}
                                        className="tugofwar-banner__avatar"
                                    />
                                </div> */}
                            </>
                        ) : (
                            <h1 className="tugofwar-banner__winner-name" style={{ color: '#fde047' }}>
                                ¡HA HABIDO UN EMPATE!
                            </h1>
                        )}
                    </div>
                </div>
            )}

            {/* Capa 8: Lluvia Efímera de Confeti DOM (40 Partículas con Auto-Destrucción a los 3s) */}
            {ephemeralConfetti.length > 0 && (
                <div className="tugofwar-confetti-container" aria-hidden="true">
                    {ephemeralConfetti.map((particle) => (
                        <div
                            key={particle.id}
                            className="tugofwar-confetti-particle"
                            style={{
                                left: particle.left,
                                backgroundColor: particle.color,
                                width: particle.size,
                                height: particle.size,
                                animationDelay: particle.delay,
                                animationDuration: particle.duration,
                                transform: `rotate(${particle.rotation})`
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

TugOfWarGame.propTypes = {
    payload: PropTypes.shape({
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object
    }),
    currentMatchState: PropTypes.object,
    theme: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    config: PropTypes.object,
    roomId: PropTypes.string
};

TugOfWarGame.defaultProps = {
    payload: null,
    currentMatchState: null,
    theme: null,
    config: null,
    roomId: null
};

export default TugOfWarGame;
